import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Skeleton, Snackbar, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import axiosInstance from '../../../axiosInstance';
import CoquilleExpert from '../../../components/Expert/CoquilleExpert';
import Fiche from '../../../components/Expert/Fiche';
import GedAnalysisPanel from '../../../components/GedFile/GedAnalysisPanel';
import { themeExpert } from '../../../theme/expert';

const COULEURS = {
  devis: 'info', facture: 'primary', clause: 'default',
  garantie: 'success', document: 'default',
};

const ETATS = { processing: 'warning', done: 'success', error: 'error' };

/**
 * La GED d'une pièce : la séparer en unités, en extraire le texte, l'analyser.
 *
 * La séparation appelle un modèle payant : elle ne part jamais seule, et
 * refaire — qui efface les unités avant de relancer — demande confirmation.
 */
function GedFileExtract() {
  const { fileId } = useParams();
  const naviguer = useNavigate();

  const [unites, setUnites] = useState([]);
  const [dernierPassage, setDernierPassage] = useState(null);
  const [expertiseId, setExpertiseId] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [separation, setSeparation] = useState(false);
  const [extractions, setExtractions] = useState(new Set());
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');

  const [consultee, setConsultee] = useState(null);
  const [analysee, setAnalysee] = useState(null);
  const [refaire, setRefaire] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get(`/ged/extract/${fileId}/`);
      setDernierPassage(data.last_run || null);
      setUnites(Array.isArray(data.units) ? data.units : []);
      if (data.expertise_id) setExpertiseId(data.expertise_id);
      setErreur('');
    } catch (e) {
      setErreur(e.response?.status === 404
        ? "Cette pièce n'existe pas, ou elle appartient à un dossier auquel vous n'avez pas accès."
        : "La GED de cette pièce n'a pas pu être chargée.");
      setUnites([]);
      setDernierPassage(null);
    } finally {
      setChargement(false);
    }
  }, [fileId]);

  useEffect(() => { charger(); }, [charger]);

  const separer = async (reinitialiser = false) => {
    setSeparation(true);
    setRefaire(false);
    setErreur('');
    try {
      const { data } = await axiosInstance.post(`/ged/extract/${fileId}/`, null,
        { params: { step: 'split',
                    ...(reinitialiser ? { reset: 'true' } : {}) } });
      setDernierPassage(data.run || null);
      setUnites(Array.isArray(data.units) ? data.units : []);
      setMessage('Séparation terminée');
    } catch {
      setErreur("La séparation n'a pas abouti.");
    } finally {
      setSeparation(false);
    }
  };

  const extraireTexte = async (unite) => {
    setExtractions((etat) => new Set(etat).add(unite.id));
    setErreur('');
    try {
      const { data } = await axiosInstance.post(`/ged/extract/${fileId}/`, null,
        { params: { step: 'extract', unit_id: unite.id } });
      setDernierPassage(data.run || null);
      setUnites(Array.isArray(data.units) ? data.units : []);
      setMessage('Texte extrait');
    } catch {
      setErreur("Le texte n'a pas pu être extrait.");
    } finally {
      setExtractions((etat) => {
        const suite = new Set(etat);
        suite.delete(unite.id);
        return suite;
      });
    }
  };

  const pages = (u) => (u.page_start && u.page_end
    ? `p. ${u.page_start} à ${u.page_end}` : '—');

  const confiance = (u) => `${Math.round((u.confidence ?? 0) * 100)} %`;

  return (
    <CoquilleExpert
      titre="Découpage du document"
      sous_titre={`Pièce nº ${fileId}`}
      actions={(
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {expertiseId && (
            <Button size="small"
                    onClick={() => naviguer(`/edit-expertise/${expertiseId}`)}>
              Revenir au dossier
            </Button>
          )}
          <Button size="small" onClick={charger}
                  disabled={chargement || separation}>
            Rafraîchir
          </Button>
          <Button size="small" variant="contained" onClick={() => separer(false)}
                  disabled={separation || chargement}>
            {separation ? 'Séparation…' : 'Séparer le document'}
          </Button>
          {unites.length > 0 && (
            <Button size="small" color="error" onClick={() => setRefaire(true)}
                    disabled={separation || chargement}>
              Refaire
            </Button>
          )}
        </Stack>
      )}
    >
      <Stack spacing={3}>
        {erreur && <Alert severity="error"
                          onClose={() => setErreur('')}>{erreur}</Alert>}

        {chargement && <Skeleton height={180} />}

        {!chargement && !dernierPassage && !erreur && (
          <Alert severity="info">
            Ce document n'a pas encore été découpé. « Séparer le document »
            demande à l'IA d'y repérer les pièces qu'il contient — devis,
            facture, clause — et de les isoler.
          </Alert>
        )}

        {dernierPassage && (
          <Fiche titre="Dernier passage">
            <Stack direction="row" spacing={2} alignItems="center"
                   flexWrap="wrap" useFlexGap>
              <Chip size="small" label={dernierPassage.status}
                    color={ETATS[dernierPassage.status] || 'default'} />
              <Typography variant="body2" color="text.secondary">
                {new Date(dernierPassage.created_at).toLocaleString('fr-BE')}
              </Typography>
            </Stack>
            {dernierPassage.logs && (
              <Box component="pre"
                   sx={{ mt: 2, mb: 0, p: 2, borderRadius: 1,
                         bgcolor: 'background.default', border: '1px solid',
                         borderColor: 'divider', whiteSpace: 'pre-wrap',
                         fontSize: '0.8rem', maxHeight: 220,
                         overflow: 'auto' }}>
                {dernierPassage.logs}
              </Box>
            )}
          </Fiche>
        )}

        {!chargement && (
          <Fiche titre={`Unités (${unites.length})`}
                 explication="Les pièces repérées dans le document. Extraire le texte d'une unité la rend citable."
                 sx={{ p: 0 }}>
            {unites.length === 0 ? (
              <Typography variant="body2" color="text.secondary"
                          sx={{ py: 4, textAlign: 'center' }}>
                Aucune unité pour l'instant.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Titre</TableCell>
                      <TableCell>Pages</TableCell>
                      <TableCell align="right">Confiance</TableCell>
                      <TableCell>Relue</TableCell>
                      <TableCell align="right">&nbsp;</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unites.map((unite) => (
                      <TableRow key={unite.id} hover>
                        <TableCell>
                          <Chip size="small" label={unite.type}
                                color={COULEURS[unite.type] || 'default'}
                                variant={COULEURS[unite.type] ? 'filled'
                                                              : 'outlined'} />
                        </TableCell>
                        <TableCell>{unite.title}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {pages(unite)}
                        </TableCell>
                        {/* Un score brut ne se lit pas : 0.87 devient 87 %. */}
                        <TableCell align="right">{confiance(unite)}</TableCell>
                        <TableCell>
                          {unite.validated_by_human
                            ? <Chip size="small" color="success" label="oui" />
                            : <Box component="span"
                                   sx={{ color: 'text.disabled' }}>non</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button size="small"
                                  onClick={() => extraireTexte(unite)}
                                  disabled={extractions.has(unite.id)}>
                            {extractions.has(unite.id) ? 'Extraction…' : 'Texte'}
                          </Button>
                          <Button size="small"
                                  onClick={() => setConsultee(unite)}
                                  disabled={!unite.extracted_text}>
                            Lire
                          </Button>
                          <Button size="small" variant="outlined"
                                  onClick={() => setAnalysee(unite)}>
                            Analyser
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Fiche>
        )}
      </Stack>

      <GedAnalysisPanel
        show={analysee !== null}
        onHide={() => setAnalysee(null)}
        unit={analysee}
        onUnitValidated={(id) => setUnites((etat) => etat.map(
          (u) => (u.id === id ? { ...u, validated_by_human: true } : u)))}
      />

      <ThemeProvider theme={themeExpert}>
        <Dialog open={consultee !== null} onClose={() => setConsultee(null)}
                fullWidth maxWidth="lg">
          <DialogTitle sx={{ pb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center"
                   flexWrap="wrap" useFlexGap>
              <span>{consultee?.title}</span>
              {consultee?.type && (
                <Chip size="small" variant="outlined" label={consultee.type} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary"
                        component="div">
              {consultee && `${pages(consultee)} — confiance ${confiance(consultee)}`}
            </Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Box component="pre"
                 sx={{ whiteSpace: 'pre-wrap', m: 0, p: 2, borderRadius: 1,
                       bgcolor: 'background.default', border: '1px solid',
                       borderColor: 'divider', fontSize: '0.85rem' }}>
              {consultee?.extracted_text || ''}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConsultee(null)}>Fermer</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={refaire} onClose={() => setRefaire(false)}>
          <DialogTitle>Refaire le découpage ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Les {unites.length} unité{unites.length > 1 ? 's' : ''} actuelle
              {unites.length > 1 ? 's' : ''}, leur texte extrait et ce qui a
              été relu seront effacés, puis le document sera re-soumis à l'IA.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setRefaire(false)}>Annuler</Button>
            <Button onClick={() => separer(true)} color="error"
                    variant="contained">
              Effacer et refaire
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>

      <Snackbar open={Boolean(message)} autoHideDuration={3000}
                onClose={() => setMessage('')} message={message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </CoquilleExpert>
  );
}

export default GedFileExtract;
